<template>
  <v-list-group
    :prepend-icon="formattedDropdown.prependIcon"
    :value="nestedItemsVisible"
  >
    <template v-slot:activator>
      <v-list-item-title>
        {{translate(formattedDropdown.translationKey)}}
      </v-list-item-title>
    </template>
    <v-list-item>
      <v-list-item-title>
      {{translate('sidebar_planning')}}: {{planningYear}}/{{planningMonth}}
      </v-list-item-title>
      <v-list-item-action>
        <v-btn icon x-small @click.stop="toggleAll" :title="allExpanded ? 'Collapse all' : 'Expand all'">
          <v-icon small>{{ allExpanded ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal' }}</v-icon>
        </v-btn>
      </v-list-item-action>
    </v-list-item>

    <v-list-group
      v-for="(group, gIdx) in formattedGroups"
      :key="'group-' + gIdx"
      :ref="'subgroup-' + gIdx"
      v-model="openGroups[gIdx]"
      sub-group
      no-action
      :group="group.routeGroup"
    >
      <template v-slot:activator>
        <v-list-item-icon>
          <v-icon small>{{ group.icon }}</v-icon>
        </v-list-item-icon>
        <v-list-item-title class="subtitle-2">{{ translate(group.groupKey) }}</v-list-item-title>
      </template>

      <router-link
        v-for="(item, idx) in group.items"
        :key="gIdx + '-' + idx"
        exact
        tag="v-list-item"
        active-class="router-link-active v-list-item--active"
        :to="item.bindedRoute"
      >
        <v-list-item-content>
          <v-list-item-title>{{translate(item.translationKey)}}</v-list-item-title>
        </v-list-item-content>
      </router-link>
      <v-list-item
        v-if="group.actions && group.actions.includes('generateReport')"
        :key="gIdx + '-action-report'"
        @click.stop="generateReport()"
        link
      >
        <v-list-item-content>
          <v-list-item-title>Generate report</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-list-item
        v-if="group.actions && group.actions.includes('generateExcel')"
        :key="gIdx + '-action-excel'"
        @click.stop="generateExcel()"
        link
      >
        <v-list-item-content>
          <v-list-item-title>Generate excel</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list-group>
  </v-list-group>
</template>

<script>
import each from 'lodash/each';
import { mapGetters, mapActions } from 'vuex';
import { supplant } from '../utilities/supplant';
import store from '../store';

export default {
  name: 'SidebarNestedItems',
  props: {
    dropdown: Object,
    supplantKeys: Object,
  },
  async created() {
    this.formatRouteBindings();
    const planning = await store.dispatch('plannings/getPlanning', this.$route.params.planningId);
    this.planningYear = planning.year;
    this.planningMonth = planning.month;
  },
  data() {
    const groups = this.dropdown.items || [];
    return {
      nestedItemsVisible: true,
      formattedDropdown: this.dropdown,
      formattedGroups: groups,
      openGroups: groups.map(() => false),
      openGroupIndexes: new Set(),
      allExpanded: false,
      planningYear: '',
      planningMonth: '',
    };
  },
  computed: {
    ...mapGetters('translations', [
      'translate',
    ]),
    ...mapActions('plannings', [
      'getPlanning',
    ]),
  },
  methods: {
    formatRouteBindings() {
      this.formattedGroups = (this.dropdown.items || []).map((group) => {
        const formattedItems = group.items.map((item) => {
          const formatted = { ...item };
          if (formatted.routeSupplantKeys) {
            each(formatted.routeSupplantKeys, (key) => {
              formatted.bindedRoute = supplant(formatted.route, {
                [key]: this.supplantKeys[key],
              });
            });
          }
          return formatted;
        });
        // Build a route group pattern from all item routes for auto-open
        const routeParts = formattedItems
          .filter(item => item.bindedRoute)
          .map(item => `${item.bindedRoute.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`);
        const routeGroup = routeParts.length ? `(${routeParts.join('|')})` : undefined;
        return { ...group, items: formattedItems, routeGroup };
      });
      this.openGroups = this.formattedGroups.map((_, index) => (
        this.openGroups[index] !== undefined ? this.openGroups[index] : false
      ));
    },
    setAllGroupsActive(active) {
      this.openGroups = this.formattedGroups.map(() => active);
      this.formattedGroups.forEach((_, gIdx) => {
        if (active) {
          this.openGroupIndexes.add(gIdx);
        } else {
          this.openGroupIndexes.delete(gIdx);
        }
      });
    },
    toggleAll() {
      this.allExpanded = !this.allExpanded;
      this.setAllGroupsActive(this.allExpanded);
    },
    generateReport() {
        const win = window.open(`${localStorage.getItem('api_link')}/pdf_reports/presentation/${this.$route.params.planningId}`, '_blank');
        win.focus();
    },
    generateExcel() {
        const randomParam = Math.floor(Math.random() * 1000000);
        const url = `${localStorage.getItem('api_link')}/excel_reports/excel/${this.$route.params.planningId}?random=${randomParam}`;
        const win = window.open(url, '_blank');
        win.focus();
      },
  },
  watch: {
    deep: true,
    supplantKeys() {
      this.formatRouteBindings();
    },
    $route: {
      handler() {
        // Sync openGroupIndexes with actual state (respects manual close)
        this.formattedGroups.forEach((_, gIdx) => {
          const ref = this.$refs[`subgroup-${gIdx}`];
          if (ref && ref[0]) {
            if (ref[0].isActive) {
              this.openGroupIndexes.add(gIdx);
            } else {
              this.openGroupIndexes.delete(gIdx);
            }
          }
        });
        // After Vuetify processes the route change, re-open previously open groups
        this.$nextTick(() => {
          this.openGroups = this.formattedGroups.map((_, gIdx) => (
            this.openGroupIndexes.has(gIdx) || this.allExpanded
          ));
        });
      },
    },
  },
};
</script>
